export type WhenKey = 'any' | 'today' | 'tomorrow' | 'weekend' | 'week' | 'month' | 'later';

export const WHEN_OPTIONS: ReadonlyArray<{ value: WhenKey; label: string }> = [
  { value: 'any', label: 'Any date' },
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'weekend', label: 'This weekend' },
  { value: 'week', label: 'Next 7 days' },
  { value: 'month', label: 'This month' },
  { value: 'later', label: 'Later' },
];

export function isWhenKey(value: string | null | undefined): value is WhenKey {
  return WHEN_OPTIONS.some((o) => o.value === value);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Local-time `YYYY-MM-DD`, matching the `date` field on EventItem. */
export const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseIsoDate = (iso: string) => new Date(`${iso}T00:00:00`);

export function isUpcomingDate(iso: string | undefined, now: Date = new Date()): boolean {
  return Boolean(iso) && (iso as string) >= toIsoDate(now);
}

/** Does a single event date fall inside the given quick-filter window? */
export function matchesWhen(iso: string, when: WhenKey, now: Date = new Date()): boolean {
  if (when === 'any') return true;
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return false;

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const add = (n: number) => {
    const t = new Date(today);
    t.setDate(t.getDate() + n);
    return t;
  };
  const inRange = (a: Date, b: Date) => d >= a && d < b;
  const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  switch (when) {
    case 'today':
      return inRange(today, add(1));
    case 'tomorrow':
      return inRange(add(1), add(2));
    case 'weekend': {
      const dow = today.getDay();
      const daysToSat = dow === 0 ? -1 : 6 - dow;
      const sat = add(daysToSat);
      const mon = new Date(sat);
      mon.setDate(sat.getDate() + 2);
      return inRange(dow === 0 ? today : sat, mon);
    }
    case 'week':
      return inRange(today, add(7));
    case 'month':
      return inRange(today, startOfNextMonth);
    case 'later':
      return d >= startOfNextMonth;
    default:
      return true;
  }
}

export function formatDayShort(iso: string): string {
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${DAYS_SHORT[d.getDay()]}, ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

export function monthShort(iso: string): string {
  const d = parseIsoDate(iso);
  return Number.isNaN(d.getTime()) ? '' : MONTHS_SHORT[d.getMonth()];
}

export function dayOfMonth(iso: string): string {
  const d = parseIsoDate(iso);
  return Number.isNaN(d.getTime()) ? '' : pad(d.getDate());
}

/** `HH:MM` (24h) -> `7:30 PM`. Returns '' when the value isn't a valid time. */
export function formatTime12(hm: string | undefined): string {
  if (!hm) return '';
  const [h, m] = hm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = ((h + 11) % 12) + 1;
  return `${hour}:${pad(m)} ${suffix}`;
}

/** "Today", "Tomorrow", "This Sat", otherwise "Sat, 14 Nov". */
export function relativeDay(iso: string, now: Date = new Date()): string {
  const d = parseIsoDate(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff > 1 && diff < 7) return `This ${DAYS_SHORT[d.getDay()]}`;
  return formatDayShort(iso);
}

export const compareEventStart = (a: { date: string; time: string }, b: { date: string; time: string }) =>
  `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`);
